import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, apiPortraitUrl } from "@/auth";

/* ============================================================
 /rappers — Public rapper directory

 Grid of every subscriber. Sort by wins / monthly / online.
 Public — no login required. Powers the PvP challenge flow.
 ============================================================ */

const SORTS = [
 { key: "wins", label: "MOST WINS" },
 { key: "monthly", label: "THIS MONTH" },
 { key: "winrate", label: "WIN RATE" },
 { key: "online", label: "ONLINE NOW" },
 { key: "recent", label: "RECENTLY ACTIVE" },
];

export default function Rappers() {
 const [sort, setSort] = useState("wins");
 const [onlineOnly, setOnlineOnly] = useState(false);
 const [rappers, setRappers] = useState([]);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 setLoading(true);
 const q = new URLSearchParams({ sort, online: onlineOnly ? "true" : "false", limit: "60" });
 axios.get(`${API}/rappers?${q}`)
 .then(r => setRappers(r.data || []))
 .catch(() => setRappers([]))
 .finally(() => setLoading(false));
 }, [sort, onlineOnly]);

 const onlineCount = rappers.filter(r => r.is_online).length;

 return (
 <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2"> THE ROSTER</div>
 <h1 className="font-display text-5xl md:text-7xl tracking-tight mb-4">
 ALL <span className="text-yt-red">RAPPERS</span>
 </h1>
 <p className="text-white/70 font-body max-w-2xl mb-6 text-lg">
 Every rapper in the arena. Click one to see their record. Challenge them if you dare.
 </p>

 {/* Filter bar */}
 <div className="flex flex-wrap items-center gap-2 mb-8">
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mr-2">SORT:</div>
 {SORTS.map(s => (
 <button key={s.key} data-testid={`rappers-sort-${s.key}`}
 onClick={() => setSort(s.key)}
 className={`px-3 py-2 text-xs font-mono tracking-widest border-2 transition-colors
 ${sort === s.key ? "border-yt-yellow bg-yt-yellow text-black" : "border-yt-border text-white hover:border-yt-yellow"}`}>
 {s.label}
 </button>
 ))}
 <button data-testid="rappers-toggle-online"
 onClick={() => setOnlineOnly(v => !v)}
 className={`ml-2 px-3 py-2 text-xs font-mono tracking-widest border-2 transition-colors
 ${onlineOnly ? "border-yt-judge bg-yt-judge text-black" : "border-yt-border text-white hover:border-yt-judge"}`}>
 {onlineOnly ? "● ONLINE ONLY" : `○ SHOW ALL (${onlineCount} online)`}
 </button>
 </div>

 {loading && <div className="font-mono text-yt-muted animate-pulse-red">LOADING...</div>}

 <div data-testid="rappers-grid" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
 {rappers.map(r => (
 <Link key={r.rapper_name} to={`/rapper/${encodeURIComponent(r.rapper_name)}`}
 data-testid={`rapper-card-${r.rapper_name}`}
 className="bg-yt-surface border border-yt-border hover:border-yt-yellow transition-colors overflow-hidden">
 <div className="aspect-[3/4] bg-black relative">
 {r.portrait ? (
 <img src={apiPortraitUrl(r.portrait)} alt={r.rapper_name} className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex items-center justify-center bg-yt-surface font-display text-4xl text-yt-yellow">{(r.name || "??").slice(0, 2).toUpperCase()}</div>
 )}
 {r.is_online && (
 <div className="absolute top-2 right-2 bg-yt-judge text-black text-[9px] px-2 py-0.5 font-mono tracking-widest">
 ● ONLINE
 </div>
 )}
 {r.tier === "champion_pass" && (
 <div className="absolute top-2 left-2 bg-yt-yellow text-black text-[9px] px-2 py-0.5 font-mono tracking-widest">
 CHAMP
 </div>
 )}
 </div>
 <div className="p-2">
 <div className="font-display text-white text-lg tracking-tight truncate">{r.rapper_name}</div>
 <div className="font-mono text-[9px] text-yt-yellow tracking-widest truncate">{r.style?.toUpperCase()}</div>
 <div className="mt-1 flex items-center justify-between font-mono text-[10px]">
 <span className="text-yt-judge">{r.wins}W</span>
 <span className="text-yt-red">{r.losses}L</span>
 <span className="text-white">{r.win_rate}%</span>
 </div>
 </div>
 </Link>
 ))}
 </div>

 {!loading && rappers.length === 0 && (
 <div className="text-center py-16 font-mono text-yt-muted">
 No rappers match this filter.
 </div>
 )}
 </div>
 );
}
